import { useRef, useState } from "react";
import { Icon } from "../../icons";
import { Popover } from "../Popover/Popover";
import { followInApp, SidebarSubLink } from "./SidebarLink";
import { containsActive, FALLBACK_MODULE_ICON, isActiveItem, moduleListId } from "./navModel";
import type { NavGroup, NavItem } from "./navTypes";

const FLYOUT_MIN_WIDTH = 200;

interface SidebarModuleProps {
  group: NavGroup;
  active?: string;
  onNavigate?: (id: string) => void;
}

function moduleButtonClass(group: NavGroup, active: string | undefined): string {
  return `nb-side-item nb-side-module-btn${containsActive(group, active) ? " is-current" : ""}`;
}

/** A module in the expanded sidebar: a button that shows or hides its sub-sections. */
export function SidebarModule({
  group,
  active,
  open,
  onToggle,
  onNavigate,
}: SidebarModuleProps & { open: boolean; onToggle: (open: boolean) => void }): React.JSX.Element {
  const listId = moduleListId(group);
  return (
    <div className={`nb-side-module${open ? " is-open" : ""}`}>
      <button
        type="button"
        className={moduleButtonClass(group, active)}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => onToggle(!open)}
      >
        <Icon name={group.icon ?? FALLBACK_MODULE_ICON} />
        <span className="nb-side-label">{group.label}</span>
        <Icon name="chevronDown" className="nb-side-chevron" />
      </button>
      <div className="nb-side-sub" id={listId} role="group" aria-label={group.label} hidden={!open}>
        {group.items.map((item) => (
          <SidebarSubLink key={item.id} item={item} active={isActiveItem(item, active)} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  );
}

/** A module in the collapsed sidebar: its icon opens a menu with the sub-sections beside it. */
export function SidebarModuleFlyout({ group, active, onNavigate }: SidebarModuleProps): React.JSX.Element {
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const closeFlyout = (): void => setFlyoutOpen(false);
  return (
    <div className="nb-side-module">
      <button
        ref={anchorRef}
        type="button"
        className={moduleButtonClass(group, active)}
        aria-label={group.label}
        aria-haspopup="menu"
        aria-expanded={flyoutOpen}
        onClick={() => setFlyoutOpen((v) => !v)}
      >
        <Icon name={group.icon ?? FALLBACK_MODULE_ICON} />
      </button>
      <Popover anchorRef={anchorRef} open={flyoutOpen} onClose={closeFlyout} side="right" role="menu" ariaLabel={group.label} minWidth={FLYOUT_MIN_WIDTH}>
        <div className="nb-menu-label">{group.label}</div>
        {group.items.map((item) => (
          <FlyoutLink
            key={item.id}
            item={item}
            selected={isActiveItem(item, active)}
            onFollow={() => {
              closeFlyout();
              onNavigate?.(item.id);
            }}
          />
        ))}
      </Popover>
    </div>
  );
}

function FlyoutLink({ item, selected, onFollow }: { item: NavItem; selected: boolean; onFollow: () => void }): React.JSX.Element {
  return (
    <a
      role="menuitem"
      href={item.href ?? "#"}
      className={`nb-menu-item${selected ? " is-selected" : ""}`}
      aria-current={selected ? "page" : undefined}
      onClick={(e) => followInApp(e, onFollow)}
    >
      <Icon name={item.icon} />
      <span className="nb-truncate">{item.label}</span>
    </a>
  );
}
